<script setup lang="ts">
import { computed, useId } from "vue";

import type { SelectOption, SelectOpts } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: string | number;
  options: ReadonlyArray<SelectOption<string | number>>;
  opts?: SelectOpts;
}>();

const emit = defineEmits<{
  (e: "change-target", id: string, value: string | number): void;
}>();

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text and points
// its `<label for>` at `controlId`.
const labelId = useId();
const controlId = useId();

const stringValue = computed(() => String(props.value));
const optionLookup = computed(() => {
  const m = new Map<string, string | number>();
  for (const o of props.options) m.set(String(o.value), o.value);
  return m;
});

// Print-mode display value: the label of the currently-selected option,
// or the raw value if no option matches. Matches the live dropdown's
// closed-state appearance, just frozen — a reader of the PDF sees the
// same string the GM would have seen above the dropdown on screen.
const printValue = computed(() => {
  const match = props.options.find(
    (o) => String(o.value) === String(props.value),
  );
  return match?.label ?? String(props.value);
});

function onChange(e: Event) {
  const raw = (e.target as HTMLSelectElement).value;
  const original = optionLookup.value.get(raw);
  if (original !== undefined) emit("change-target", props.id, original);
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="select">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="select-label"
      :label="opts?.label"
      :description="opts?.description"
      :for-id="controlId"
      :label-id="labelId"
    />
    <select
      :id="controlId"
      :value="stringValue"
      :disabled="options.length === 0"
      @change="onChange"
    >
      <!--
        Empty-state branch — `options` populated dynamically from a
        query that returned zero rows. Render a single disabled
        placeholder option so the control still reads as a dropdown
        ("nothing to pick") rather than a clickable blank box.
      -->
      <option v-if="options.length === 0" disabled value="">
        {{ opts?.placeholder ?? "No options" }}
      </option>
      <option
        v-for="o in options"
        v-else
        :key="String(o.value)"
        :value="String(o.value)"
      >
        {{ o.label }}
      </option>
    </select>
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.select {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .select {
    display: flex;
    width: 100%;
  }
}
.select-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
select {
  appearance: none;
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 6px 28px 6px 10px;
  font: inherit;
  cursor: pointer;
  /* A native select sizes to its longest option; cap it to the slot
     and let the closed state ellipsise. */
  max-width: 100%;
  min-width: 0;
  text-overflow: ellipsis;
  touch-action: manipulation;
  background-image: linear-gradient(45deg, transparent 50%, currentColor 50%),
    linear-gradient(135deg, currentColor 50%, transparent 50%);
  background-position: calc(100% - 14px) center, calc(100% - 9px) center;
  background-size: 5px 5px;
  background-repeat: no-repeat;
}
select:focus {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  select {
    min-height: 44px;
    font-size: 16px;
  }
}
</style>
